<script setup lang="ts">
import { computed, ref } from 'vue';
import { ArrowsPointingOutIcon } from '@heroicons/vue/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import { LazyTooltip } from '@/components/ui/tooltip';
import CoverArt from '@/components/CoverArt.vue';
import PlayTrackButton from '@/components/PlayTrackButton.vue';
import { UNKNOWN_ALBUM_ART_PATH } from '@/constants/coverArt';

// The `play` bag is intentionally loose (Record) so the whole cover chain stays
// untyped-by-convention; cast to PlayTrackButton's own props at the spread so the
// required discriminant (`kind`) is recognized.
type PlayTrackButtonProps = InstanceType<typeof PlayTrackButton>['$props'];

/**
 * Cover art with an optional "play overlay + expand-to-zoom" treatment.
 *
 * Wraps {@link CoverArt} (which already zooms on click/hover) and, when a
 * `play` prop is supplied, layers the same overlay TrackCard used to hand-roll:
 * a full-bleed {@link PlayTrackButton} (`variant="cover-overlay"`) so clicking
 * the art plays the track, plus a bottom-right expand button that opens the
 * full-art zoom. The expand button exists because the play overlay intercepts
 * the cover click, so the zoom needs its own control.
 *
 * Without `play`, this renders just `<CoverArt>` — identical to using CoverArt
 * directly — so every cover in the app can unify on this one component while
 * the playable/zoomable surfaces opt in via `play`.
 */
const {
    src = null,
    alt = 'Cover art',
    size = 96,
    previewSize = 480,
    fallbackSrc = UNKNOWN_ALBUM_ART_PATH,
    class: className = '',
    play = undefined,
    expandTestid = undefined,
    failureReason = null,
    failureTestid = undefined,
} = defineProps<{
    // Pass-through to CoverArt.
    src?: string | null;
    alt?: string;
    size?: number;
    previewSize?: number;
    fallbackSrc?: string;
    class?: string;
    /**
     * A bag of {@link PlayTrackButton} props (kind, liveTrackCode, hasAudio,
     * title, artist, album, coverUrl, …). When provided, the cover becomes a
     * full-bleed play target with an expand-to-zoom button; when omitted, only
     * the plain (click-to-zoom) cover renders.
     */
    play?: Record<string, unknown>;
    /** `data-testid` for the expand-cover button (only used when `play` is set). */
    expandTestid?: string;
    /**
     * Why this track would not play. Renders a badge on the corner of the art.
     *
     * On the ARTWORK, and absolutely positioned, for one reason: it must not
     * change the layout. The play queue used to put this icon in the row's flex
     * flow, so a failing row's card was ~26px narrower than a healthy one and
     * the list visibly jumped as tracks failed. Out of flow, a broken row and a
     * working row are the same shape — and the marker sits on the thing that
     * actually failed.
     */
    failureReason?: string | null;
    /** `data-testid` for the failure badge. */
    failureTestid?: string;
}>();

const coverArtRef = ref<InstanceType<typeof CoverArt> | null>(null);

/**
 * Small tiles (the 44px search/currently-playing thumbs) get a 20px play chip
 * and a 14px corner expand button — the default 28px + 20px pair overlaps on
 * anything this size.
 */
const small = computed(() => size <= 48);

/** Forwarded from CoverArt so the play overlay's own expand button (and consumers) can open the zoom. */
defineExpose({
    openPreview: () => coverArtRef.value?.openPreview(),
});
</script>

<template>
    <div class="group relative inline-block" :class="className">
        <!--
        One wrapper for every case, rather than three branches.

        `group relative` is unconditional now. It was previously added only when
        `play` was set, which meant the failure-badge branch had to re-create it —
        and that copy put the caller's `class` on the inner CoverArt instead of on
        the wrapper. The wrapper is the flex item, so the queue's bare row passing
        `shrink-0` lost it the moment a track failed: a failing entry with a long
        title squashed its 36px cover while the working row beside it held its
        width. That is exactly the layout stability the badge was moved onto the
        artwork to protect, reintroduced by a different route.

        It also had the badge markup written twice, which is the kind of thing
        that drifts.
        -->
        <CoverArt
            ref="coverArtRef"
            :src="src"
            :alt="alt"
            :size="size"
            :preview-size="previewSize"
            :fallback-src="fallbackSrc"
        />
        <template v-if="play">
            <PlayTrackButton
                variant="cover-overlay"
                :chip-size="small ? 'sm' : 'default'"
                v-bind="(play as unknown as PlayTrackButtonProps)"
            />
            <button
                type="button"
                class="bg-background/85 border-border absolute z-10 flex cursor-pointer items-center justify-center rounded-full border opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
                :class="small ? 'right-0.5 bottom-0.5 h-3.5 w-3.5' : 'right-1 bottom-1 h-5 w-5'"
                aria-label="View full cover art"
                :data-testid="expandTestid"
                @click.stop="coverArtRef?.openPreview()"
            >
                <ArrowsPointingOutIcon :class="small ? 'h-2.5 w-2.5' : 'h-3 w-3'" />
            </button>
        </template>

        <!-- Absolutely positioned, so a failing tile is the same size and shape
             as a working one. Lazy tooltip: a queue can hold 500 of these. -->
        <LazyTooltip v-if="failureReason" side="top">
            <span
                class="bg-destructive text-destructive-foreground absolute -top-1 -right-1 z-20 flex items-center justify-center rounded-full shadow-sm"
                :class="small ? 'h-3.5 w-3.5' : 'h-[18px] w-[18px]'"
                :data-testid="failureTestid"
                role="img"
                :aria-label="`Could not play this track: ${failureReason}`"
            >
                <ExclamationTriangleIcon :class="small ? 'h-2 w-2' : 'h-2.5 w-2.5'" aria-hidden="true" />
            </span>
            <template #content>{{ failureReason }}</template>
        </LazyTooltip>
    </div>
</template>
